import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, BookOpen } from 'lucide-react';
import Tip from '../../components/Tip';

const API_URL = process.env.REACT_APP_API_URL || 'https://tradesavvy-production.up.railway.app';

interface WatchlistItem {
  pair: string;
  symbol: string;
  name: string;
}

interface TakeProfit {
  price: number;
  label: string;
}

interface TimeframeResult {
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  entry_price: number | null;
  stop_loss: number | null;
  take_profit: number | null;
  take_profits: TakeProfit[];
  risk_reward: number | null;
  conviction: 'HIGH' | 'LOW' | null;
  supertrend_direction: 'BULLISH' | 'BEARISH';
  supertrend_line: number | null;
  rsi9: number | null;
  cmo: number | null;
  bars_since_flip: number | null;
  volume_ratio: number | null;
  atr_expanding: boolean;
  error?: string;
}

interface CoinResult {
  symbol: string;
  price: number;
  change24h: number;
  timeframes: Record<string, TimeframeResult>;
}

interface UAlgoTabProps {
  watchlist: WatchlistItem[];
  apiUrl?: string;
}

const TIMEFRAMES = ['1h', '4h', '1d'];

const UAlgoTab: React.FC<UAlgoTabProps> = ({ watchlist, apiUrl }) => {
  const [data, setData] = useState<CoinResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTf, setSelectedTf] = useState('4h');
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const url = apiUrl || API_URL;

  const fetchData = useCallback(async () => {
    if (watchlist.length === 0) return;
    setLoading(true);
    try {
      const symbols = watchlist.map(w => w.pair).join(',');
      const response = await fetch(`${url}/api/signals/ualgo?symbols=${symbols}`, {
        signal: AbortSignal.timeout(60000),
      });
      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('UAlgo fetch error:', e);
    }
    setLoading(false);
  }, [watchlist, url]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const nameMap: Record<string, string> = {};
  watchlist.forEach(w => { nameMap[w.symbol] = w.name; });

  const formatPrice = (price: number): string => {
    if (price === 0) return '0.00';
    if (price >= 1) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const decimals = Math.max(2, -Math.floor(Math.log10(price)) + 2);
    return price.toFixed(Math.min(decimals, 8));
  };

  const getSignalColor = (signal: string) => {
    if (signal === 'BUY') return 'text-green-500';
    if (signal === 'SELL') return 'text-red-500';
    return 'text-gray-400';
  };

  const getSignalIcon = (signal: string) => {
    if (signal === 'BUY') return <TrendingUp className="w-5 h-5" />;
    if (signal === 'SELL') return <TrendingDown className="w-5 h-5" />;
    return <Minus className="w-5 h-5" />;
  };

  const getRRColor = (rr: number) => {
    if (rr >= 3) return 'text-green-400';
    if (rr >= 2) return 'text-green-500';
    if (rr >= 1.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getRsiColor = (rsi: number) => {
    if (rsi <= 30) return 'text-green-400';
    if (rsi <= 40) return 'text-green-500';
    if (rsi >= 70) return 'text-red-400';
    if (rsi >= 60) return 'text-red-500';
    return 'text-gray-300';
  };

  const getCmoColor = (cmo: number) => {
    if (cmo >= 20) return 'text-green-400';
    if (cmo >= 0) return 'text-green-500';
    if (cmo <= -20) return 'text-red-400';
    if (cmo < 0) return 'text-red-500';
    return 'text-gray-300';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="flex gap-1.5">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              onClick={() => setSelectedTf(tf)}
              className={`px-3 py-1.5 rounded-md text-sm font-mono font-semibold transition-all ${
                selectedTf === tf
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-gray-800 text-gray-500 border border-gray-700 hover:text-white hover:border-gray-500'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded text-xs text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-blue-500/5 border border-blue-500/10 rounded-lg">
        <span className="text-xs text-gray-400">Supertrend + CMO + RSI(9) + Pivot detection, R-multiple TPs</span>
      </div>

      {lastRefresh && (
        <div style={{ marginBottom: 12, fontSize: 11, color: '#52525b' }}>
          Updated: {lastRefresh.toLocaleTimeString()} &middot; Auto-refreshes every 60s
        </div>
      )}

      {loading && data.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 text-blue-500 animate-spin mr-3" />
          <span className="text-gray-400 text-sm">Analyzing UAlgo signals...</span>
        </div>
      )}

      <div className="grid gap-5">
        {data.map(coin => {
          const tfData = coin.timeframes?.[selectedTf];
          if (!tfData) return null;

          return (
            <div key={coin.symbol} className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-colors">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{coin.symbol}-USD</h2>
                  <p className="text-gray-400 text-xs">{nameMap[coin.symbol] || coin.symbol}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-mono">${formatPrice(coin.price)}</div>
                  <div className={`text-xs ${coin.change24h >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Supertrend + Indicators */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Supertrend &amp; Signal <Tip text="Supertrend (ATR 14, mult 2.0) determines trend direction. Signal fires when trend flips + RSI/CMO/pivot conditions align." /></div>
                  <div className={`flex items-center gap-2 mb-3 ${getSignalColor(tfData.signal)}`}>
                    {getSignalIcon(tfData.signal)}
                    <span className="font-bold">{tfData.signal}</span>
                    <span className="ml-auto text-xs text-gray-400">Conf <span className="font-mono">{tfData.confidence}%</span></span>
                  </div>

                  {/* Supertrend direction */}
                  <div className={`text-xs px-2 py-1 rounded text-center mb-3 ${
                    tfData.supertrend_direction === 'BULLISH'
                      ? 'bg-green-500/10 text-green-500'
                      : 'bg-red-500/10 text-red-500'
                  }`}>
                    ST: {tfData.supertrend_direction}
                    {tfData.supertrend_line && (
                      <span className="ml-1 opacity-70">(${formatPrice(tfData.supertrend_line)})</span>
                    )}
                  </div>

                  {/* Bars since flip */}
                  {tfData.bars_since_flip != null && (
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">Bars since flip <Tip text="How many bars since the last Supertrend direction change. 0 = just flipped (strongest signal). Within 5 bars = valid entry window." /></span>
                      <span className={`font-mono ${tfData.bars_since_flip <= 1 ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {tfData.bars_since_flip}
                      </span>
                    </div>
                  )}

                  {/* Volume ratio */}
                  {tfData.volume_ratio != null && (
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">Volume vs Avg</span>
                      <span className={`font-mono ${tfData.volume_ratio > 1.5 ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {tfData.volume_ratio}x{tfData.volume_ratio > 1.5 ? ' SPIKE' : ''}
                      </span>
                    </div>
                  )}

                  {/* ATR expanding */}
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">ATR <Tip text="ATR expanding = volatility increasing, often confirms breakout. Contracting = momentum fading." /></span>
                    <span className={`font-mono ${tfData.atr_expanding ? 'text-green-400' : 'text-gray-500'}`}>
                      {tfData.atr_expanding ? 'Expanding' : 'Contracting'}
                    </span>
                  </div>
                </div>

                {/* RSI + CMO Indicators */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="text-xs text-gray-400 uppercase mb-2">Momentum Filters <Tip text="RSI(9) and CMO(14) must align with Supertrend direction. For buys: RSI not overbought + CMO not deeply negative. For sells: RSI not oversold + CMO not deeply positive." /></div>

                  {/* RSI gauge */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">RSI(9)</span>
                      <span className={`font-mono font-bold ${getRsiColor(tfData.rsi9 || 50)}`}>
                        {tfData.rsi9 != null ? tfData.rsi9.toFixed(1) : '--'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 relative">
                      <div className="absolute inset-0 flex">
                        <div className="w-[30%] bg-green-500/20 rounded-l-full" />
                        <div className="w-[40%] bg-gray-600/30" />
                        <div className="w-[30%] bg-red-500/20 rounded-r-full" />
                      </div>
                      {tfData.rsi9 != null && (
                        <div
                          className="absolute top-0 w-1 h-2 bg-white rounded-full"
                          style={{ left: `${Math.min(100, Math.max(0, tfData.rsi9))}%` }}
                        />
                      )}
                    </div>
                    <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
                      <span>Oversold</span>
                      <span>Overbought</span>
                    </div>
                  </div>

                  {/* CMO gauge */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">CMO(14) <Tip text="Chande Momentum Oscillator. Range -100 to +100. Positive = bullish momentum, negative = bearish. Combined with HMA for smoothing." /></span>
                      <span className={`font-mono font-bold ${getCmoColor(tfData.cmo || 0)}`}>
                        {tfData.cmo != null ? tfData.cmo.toFixed(1) : '--'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2 relative">
                      <div className="absolute inset-0 flex">
                        <div className="w-1/2 bg-red-500/15 rounded-l-full" />
                        <div className="w-1/2 bg-green-500/15 rounded-r-full" />
                      </div>
                      {tfData.cmo != null && (
                        <div
                          className="absolute top-0 w-1 h-2 bg-white rounded-full"
                          style={{ left: `${Math.min(100, Math.max(0, (tfData.cmo + 100) / 2))}%` }}
                        />
                      )}
                    </div>
                    <div className="flex justify-between text-[9px] text-gray-600 mt-0.5">
                      <span>-100 Bearish</span>
                      <span>+100 Bullish</span>
                    </div>
                  </div>
                </div>

                {/* Trade Setup */}
                <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-400 uppercase">Trade Setup <Tip text="Fixed % stop loss with R-multiple take profits: TP1 at 1R (25%), TP2 at 2R (25%), TP3 at 3R (50% runner)." /></span>
                    {tfData.conviction === 'HIGH' && (
                      <span className="px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded text-[10px] font-bold border border-green-500/20">HIGH CONVICTION</span>
                    )}
                    {tfData.conviction === 'LOW' && (
                      <span className="px-1.5 py-0.5 bg-yellow-500/15 text-yellow-500 rounded text-[10px] font-bold border border-yellow-500/20">LOW CONVICTION</span>
                    )}
                  </div>
                  {tfData.entry_price && tfData.risk_reward ? (
                    <div>
                      {/* R:R large display */}
                      <div className="text-center mb-3">
                        <div className={`text-3xl font-bold font-mono ${getRRColor(tfData.risk_reward)}`}>
                          {tfData.risk_reward.toFixed(1)}R
                        </div>
                        <div className="text-[10px] text-gray-500">Risk/Reward (to TP1)</div>
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Entry</span>
                          <span className="font-mono text-blue-400">${formatPrice(tfData.entry_price)}</span>
                        </div>
                        {tfData.stop_loss && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Stop Loss</span>
                            <span className="font-mono text-red-400">${formatPrice(tfData.stop_loss)}</span>
                          </div>
                        )}
                        {tfData.take_profits?.map((tp, i) => (
                          <div key={i} className="flex justify-between">
                            <span className="text-gray-500">TP{i + 1} <span className="text-gray-600">({tp.label})</span></span>
                            <span className="font-mono text-green-400">${formatPrice(tp.price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-28">
                      <div className="text-center">
                        <div className="text-xs text-gray-600 mb-1">No active setup</div>
                        <div className="text-[10px] text-gray-700">
                          Waiting for Supertrend flip + filter alignment
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UAlgoTab;
